import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Desktop: ← / → keyboard switches gallery photos while the gallery is hovered/focused.
    if (!this.hasAttribute('tabindex')) this.tabIndex = 0;
    this.addEventListener('pointerenter', () => {
      this.#pointerInside = true;
    }, { signal });
    this.addEventListener('pointerleave', () => {
      this.#pointerInside = false;
    }, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('keydown', this.#handleDocumentKeydown, { signal });
  }

  #controller = new AbortController();
  #pointerInside = false;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Same product photos (e.g. Set of 9 ↔ Set of 12) — keep the live gallery
        // to avoid a visible flash from remounting identical media.
        if (this.#isSameMedia(newMediaGallery)) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @param {Element} other
   * @returns {boolean}
   */
  #isSameMedia(other) {
    const currentSignature = this.#mediaSignature(this);
    const nextSignature = this.#mediaSignature(other);
    return Boolean(currentSignature) && currentSignature === nextSignature;
  }

  /**
   * @param {Element} root
   * @returns {string}
   */
  #mediaSignature(root) {
    const mediaIds = [...root.querySelectorAll('[data-media-id]')]
      .map((el) => el.getAttribute('data-media-id') || '')
      .filter(Boolean);

    if (mediaIds.length) return `id:${mediaIds.join('|')}`;

    const imageSources = [...root.querySelectorAll('img')]
      .map((img) => {
        const src = img.getAttribute('src') || img.getAttribute('srcset') || '';
        // Ignore width/query noise; keep the CDN file path.
        return src.split('?')[0].replace(/_\d+x(?=\.|$)/, '');
      })
      .filter(Boolean);

    return imageSources.length ? `src:${imageSources.join('|')}` : '';
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    this.#navigateWithKeyboard(event);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeydown = (event) => {
    if (!this.#pointerInside && document.activeElement !== this) return;
    this.#navigateWithKeyboard(event);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #navigateWithKeyboard(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (!(event.target instanceof Element)) return;
    // Don't steal arrows from inputs/textareas inside the section.
    if (event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
    // Only on desktop-sized viewports.
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.slideshow?.next(event);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.slideshow?.previous(event);
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
